import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { AppShell } from "@chef/feature-app-shell";
import { setNudgeSeen, useBillingQuery } from "@chef/state-management";
import {
  getBasketFinances,
  getBasketForCalendarWeek,
} from "@chef/state-management/helpers";
import {
  CurrencyWrapper,
  PreferenceQuizNudge,
  ShoppingCart,
} from "@chef/smart-components";
import { ReceiptDefault } from "@chef/icons/small";
import { useLoyalty } from "@chef/state-management/hooks";
import { useCalendarData } from "@chef/state-management/hooks";
import { Card, Disclosure } from "@chef/components";
import {
  IPromotion,
  ISection,
  IWeekOption,
  useWeekOptionsData,
} from "@chef/feature-sanity";
import { isAccountActive } from "@chef/helpers";
import { isNotEmptyArray } from "@chef/utils/array";

import { WeekStatus } from "../lib/WeekStatus";
import { WeekOverviewDefault } from "../lib/WeekOverviewDefault";
import { CampaignDisclaimer } from "../lib/CampaignDisclaimer";
import { StandaloneGoodsDisclosure } from "../lib/StandaloneGoodsDisclosure";
import { PreferenceOverview } from "../lib/PreferenceOverview";
import { filterPromotionsByDate } from "../utils";
import { WeeklyCampaign } from "../lib/WeeklyCampaign";

import { intl } from "./MyMenu.Intl";

interface MainDefaultProps {
  week: number;
  year: number;
  promotion: IPromotion | null;
  whyDeliveryChangeSection: ISection | null;
  weekOptions: IWeekOption[];
}

export const MainDefault = ({
  week,
  year,
  promotion,
  whyDeliveryChangeSection,
  weekOptions,
}: MainDefaultProps) => {
  const dispatch = useDispatch();
  const { data: billingQuery } = useBillingQuery();

  const accountIsActive = isAccountActive(billingQuery?.billing.status || 0);

  const { calendar, basket } = useCalendarData({
    week,
    year,
  });

  const [promotions] = useWeekOptionsData(weekOptions);

  const displayedPromotions = filterPromotionsByDate(promotions, week);

  const hideNudgeWhenNudgeIsClicked = () => {
    dispatch(setNudgeSeen({ week, year }));
  };

  useEffect(() => {
    return () => {
      hideNudgeWhenNudgeIsClicked();
    };
  }, []);

  const firstCalendarWeek = calendar?.find((c) => c.week === week);

  const { data: loyaltyData } = useLoyalty();

  const basketProducts = [...basket.mealboxBasket, ...basket.groceryBasket];

  const basketPrice = firstCalendarWeek
    ? getBasketFinances({
        basketProducts,
        customerFee: getBasketForCalendarWeek({
          calendarWeek: firstCalendarWeek,
        })?.mealboxBasket?.shippingDetails.plannedDeliveryInformation
          .customerFee,
        loyaltyLevel: loyaltyData?.currentLevel,
      }).total
    : null;

  const isPreselectorBasketPopulated = isNotEmptyArray(
    firstCalendarWeek?.preselectorBasket.variationIds ?? [],
  );

  return (
    <>
      <WeekStatus
        week={week}
        year={year}
        sanityData={whyDeliveryChangeSection}
      />

      <AppShell.Main grid className="pb-12">
        <CampaignDisclaimer week={week} year={year} promotion={promotion} />

        <PreferenceQuizNudge />

        <WeeklyCampaign promotions={displayedPromotions} />

        <WeekOverviewDefault week={week} year={year} />

        {accountIsActive && (
          <div className="flex flex-col gap-4 col-span-full md:col-span-6 xl:col-start-5 md:col-start-2 xl:col-span-4">
            <div className="h-4 md:h-6 col-span-full" />
            <h2 className="text-xl">
              <strong>{intl.DETAILS}</strong>
            </h2>

            {/*
            TODO: Add back in when data is available on PickAndMix
            <BasicGoodsDisclosure week={week} year={year} />
            */}

            <StandaloneGoodsDisclosure week={week} year={year} />
            {isPreselectorBasketPopulated && (
              <Card className="!p-0">
                <Disclosure
                  className="py-4 pr-4"
                  id="order-value-disclosure"
                  Title={() => (
                    <div className="flex flex-row justify-between text-base grow">
                      <strong>{intl.ORDER_VALUE}</strong>
                      {basketPrice && (
                        <CurrencyWrapper className="mr-2">
                          {basketPrice}
                        </CurrencyWrapper>
                      )}
                    </div>
                  )}
                  Icon={() => <ReceiptDefault className="w-6 h-6 ml-4 mr-3" />}
                  ExpandedTitle={() => (
                    <strong className="text-base">{intl.ORDER_VALUE}</strong>
                  )}
                >
                  {firstCalendarWeek && (
                    <div className="px-4 pb-4">
                      <ShoppingCart calendarWeek={firstCalendarWeek} />
                    </div>
                  )}
                </Disclosure>
              </Card>
            )}
          </div>
        )}
      </AppShell.Main>
      <PreferenceOverview />
    </>
  );
};
